import { useEffect, useState } from 'react';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useSubscriptionPrices } from '@/hooks/useSubscriptionPrices';
import { trackFacebookEvent, trackTikTokEvent } from '@/lib/adsTracking';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { useSubscriptionFlags } from './useSubscriptionFlags';
import { useUser } from '@/queries/useUser';
import { hasActiveSubscription } from '../logic/subscriptionStatus.logic';
import { SUBSCRIPTION_STORAGE_KEYS } from '../constants';

export type SubscriptionInterval = 'monthly' | 'annual';

export const usePlanSelection = () => {
    const { push } = useStanRouter();
    const flags = useSubscriptionFlags();
    const { user, refetchUser } = useUser();
    const {
        currency,
        creatorMonthlyPrice,
        creatorAnnualPrice,
        creatorMonthlyAnnualizedPrice,
        creatorMonthlyPriceId,
        creatorAnnualPriceId,
    } = useSubscriptionPrices();

    // Determine which options to show based on flags
    const showMonthly = flags.show_monthly !== false || flags.show_annual === flags.show_monthly;
    const showAnnual = flags.show_annual !== false || flags.show_annual === flags.show_monthly;

    // Initialize with monthly selected (matching Vue behavior)
    const [selectedInterval, setSelectedInterval] = useState<SubscriptionInterval>('monthly');
    const [creditCardRequired, setCreditCardRequired] = useState(true);
    const [promoId, setPromoId] = useState<string | undefined>(undefined);
    const [discountPercent, setDiscountPercent] = useState<number | undefined>(undefined);
    const [promoTrialDays, setPromoTrialDays] = useState<number | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);

    const hasActiveSubscriptionStatus = hasActiveSubscription(user);

    // Redirect if user already has active subscription
    useEffect(() => {
        if (hasActiveSubscriptionStatus) {
            refetchUser()?.then(result => {
                if (result.data && hasActiveSubscription(result.data)) {
                    preventBackButton('/dashboard');
                    push('/dashboard');
                    return;
                }
                setIsLoading(false);
            });
        } else {
            setIsLoading(false);
        }
    }, [hasActiveSubscriptionStatus, push, refetchUser]);

    // Track page view on mount
    useEffect(() => {
        trackFacebookEvent('InitiateCheckout', {
            content_category: 'Signup Process',
            value: creatorMonthlyPrice,
            currency,
        });
        trackTikTokEvent('InitiateCheckout');
        stanAnalytics('pixel-InitiateCheckout');
    }, []);

    const preventBackButton = (url: string) => {
        for (let i = 0; i < 10; i++) window.history.pushState({}, String(i), url);
    };

    const handleIntervalChange = (interval: SubscriptionInterval) => {
        setSelectedInterval(interval);
    };

    const handlePromoApplied = (
        appliedPromoId: string,
        isFreeSubscription: boolean,
        appliedDiscountPercent?: number,
        appliedTrialDays?: number
    ) => {
        setPromoId(appliedPromoId || undefined);
        setCreditCardRequired(!isFreeSubscription);
        setDiscountPercent(appliedDiscountPercent);
        setPromoTrialDays(appliedTrialDays);
    };

    // Use promo trial days if set, otherwise use flags trial days
    const effectiveTrialDays = promoTrialDays ?? flags.trialDays;

    const onSubmit = () => {
        const priceId = selectedInterval === 'annual' ? creatorAnnualPriceId : creatorMonthlyPriceId;

        if (!priceId) return;

        // Save to localStorage
        localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.PRICE_ID, priceId);
        localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.TRIAL_DAYS, effectiveTrialDays.toString());
        localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.CREDIT_CARD_REQUIRED, creditCardRequired.toString());
        if (promoId) {
            localStorage.setItem(SUBSCRIPTION_STORAGE_KEYS.PROMO_ID, promoId);
        } else {
            localStorage.removeItem(SUBSCRIPTION_STORAGE_KEYS.PROMO_ID);
        }

        push('/subscribe/start');
    };

    // Calculate savings for annual plan (20% off)
    const annualSavings = Math.round(creatorMonthlyAnnualizedPrice - creatorAnnualPrice);

    // Calculate discounted prices when a promo code is applied
    const getDiscountedPrice = (price: number) => {
        if (!discountPercent || discountPercent <= 0) return price;
        return Math.round(price - (price * discountPercent) / 100);
    };

    const discountedMonthlyPrice = getDiscountedPrice(creatorMonthlyPrice);
    const discountedAnnualPrice = getDiscountedPrice(creatorAnnualPrice);

    return {
        // State
        isLoading,
        selectedInterval,
        handleIntervalChange,
        onSubmit,

        // Promo code
        showPromo: flags.show_promo ?? false,
        discountCode: flags.discount_code,
        handlePromoApplied,
        discountPercent,

        // Visibility flags
        showMonthly,
        showAnnual,

        // Pricing data
        currency,
        monthlyPrice: discountedMonthlyPrice,
        annualPrice: discountedAnnualPrice,
        // Original prices (before discount) for showing strikethrough
        originalMonthlyPrice: discountPercent ? creatorMonthlyPrice : undefined,
        originalAnnualPrice: discountPercent ? creatorAnnualPrice : undefined,
        monthlyAnnualizedPrice: creatorMonthlyAnnualizedPrice,
        annualSavings,

        // User data
        hasPastSubscription: user?.has_subscription_history ?? false,
        trialDays: effectiveTrialDays,
    };
};
